/**
 * Version: V0.0.3.0
 * Created: 2026-06-13
 * Updated: 2026-06-13
 * Description: Standalone splash loading screen page with brand logo, tagline, and animated progress loader.
 */

import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Logo from '../components/common/Logo';

export default function SplashPage({ onFinished = () => {} }) {
  const { t } = useLanguage();

  // Simulated progress timer to transition entry
  useEffect(() => {
    const timer = setTimeout(() => {
      onFinished();
    }, 2500);
    return () => clearTimeout(timer);
  }, [onFinished]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[var(--color-background)] font-sans relative overflow-hidden p-6 transition-colors duration-300">
      
      {/* Background Radial Glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none z-0 opacity-80"
        style={{
          background: 'radial-gradient(circle, var(--color-glow) 0%, rgba(15, 12, 27, 0) 70%)',
          animation: 'pulse 6s infinite alternate ease-in-out',
        }}
      />

      <style>{`
        @keyframes pulse {
          0% { opacity: 0.6; transform: translate(-50%, -50%) scale(0.95); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1.05); }
        }
        @keyframes logo-pulse {
          0% { transform: scale(1); filter: drop-shadow(0 0 10px rgba(109, 40, 217, 0.25)); }
          50% { transform: scale(1.06); filter: drop-shadow(0 0 25px rgba(109, 40, 217, 0.6)); }
          100% { transform: scale(1); filter: drop-shadow(0 0 10px rgba(109, 40, 217, 0.25)); }
        }
        @keyframes slide-progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>

      {/* Brand logo (pulsing animation) */}
      <div 
        className="text-[var(--logo-color)] z-10"
        style={{ animation: 'logo-pulse 2s infinite ease-in-out' }}
      >
        <Logo size={200} isMinimized={true} />
      </div>

      {/* Brand Name */}
      <h1 className="text-4xl font-extrabold text-[var(--logo-color)] mt-6 tracking-tight z-10 font-sans transition-colors duration-300">
        {t('nav_brand')}
      </h1>

      {/* Smooth animated progress track indicator */}
      <div className="w-[180px] h-1 bg-[var(--card-border)] rounded-full mt-6 overflow-hidden z-10">
        <div 
          className="h-full w-3/5 bg-[var(--color-primary)] rounded-full"
          style={{ animation: 'slide-progress 2s infinite ease-in-out' }}
        />
      </div>

    </div>
  );
}
